/**
 * Пометка источника — ОДНИМ компонентом на все экраны.
 *
 * Правило спеки: экран, работающий на фикстурах, печатает это видимо. Если бы
 * пометку ставил каждый экран у себя, она разошлась бы словами и пропала бы
 * там, где автор о ней забыл. Здесь её ставит оболочка по объявлению маршрута
 * (`routes.ts`), поэтому забыть нельзя: пометка появляется вместе с записью в
 * таблице источников.
 */

import { FlaskConical } from 'lucide-react';
import type { RouteSource } from './types';
import { defineMessages, type Message } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import './fixture-notice.css';

const m = defineMessages({
  noSource: {
    id: 'data.fixtureNotice.noSource',
    defaultMessage: 'демонстрационные данные (фикстуры), источника в базе района нет',
    description: 'Жирное начало пометки под шапкой экрана, который показывает демонстрационные данные: в базе района таких данных вообще нет. Дальше идёт « — » и пояснение экрана',
  },
  pendingApi: {
    id: 'data.fixtureNotice.pendingApi',
    defaultMessage: 'демонстрационные данные (фикстуры), источник в базе района ещё не подключён',
    description: 'Жирное начало пометки под шапкой экрана с демонстрационными данными: данные в базе района будут, но экран к ним ещё не подключён',
  },
});

const HEAD = { 'no-canonical-source': m.noSource, 'pending-api': m.pendingApi } as const;

/**
 * Пояснение экрана — `RouteSource.note` из `routes.ts`. Там оно остаётся русской
 * строкой: его читает и индексатор ИИ (`scripts/build-ai-surface.mjs`, Node без
 * сборки), поэтому переводимым сообщением оно быть не может. Человеку же его
 * показывает этот компонент — по сообщению маршрута; маршрута нет в таблице —
 * строка объявления как есть. Полноту и совпадение русского текста с объявлением
 * держит `FixtureNotice.test.tsx`.
 */
export const NOTES: Record<string, Message> = defineMessages({
  '/explore': {
    id: 'data.fixtureNotice.note.explore',
    defaultMessage: 'разбор ждёт свободной выборки поверх /ai/*',
    description: 'Пояснение под жирным началом пометки экрана «Разбор» (демо-данные): чего экрану не хватает; строчными, без точки; /ai/* — адреса серверного ИИ-слоя, не переводятся',
  },
  '/reports/pakhta-galla': {
    id: 'data.fixtureNotice.note.pakhtaGalla',
    defaultMessage: 'результаты сезона (урожай и затраты) в базе района не хранятся',
    description: 'Пояснение под жирным началом пометки экрана «Пахта-ғалла: баланс размещения»: этих данных в базе района нет; строчными, без точки; «база района» — основная база',
  },
  '/equipment': {
    id: 'data.fixtureNotice.note.equipment',
    defaultMessage: 'техники как сущности в базе района нет',
    description: 'Пояснение под жирным началом пометки экрана «Техника»: в базе района техники нет; строчными, без точки; «база района» — основная база',
  },
});

function noteOf(route: RouteSource, t: ReturnType<typeof useT>): string {
  const message = Object.prototype.hasOwnProperty.call(NOTES, route.path) ? NOTES[route.path] : undefined;
  return message ? t(message as never) : route.note;
}

export function FixtureNotice({ route }: { route: RouteSource | null }) {
  const t = useT();
  if (!route || route.source !== 'fixtures') return null;
  const reason = route.reason ?? 'no-canonical-source';
  return (
    <p className="fixture-notice" role="note">
      <FlaskConical size={15} aria-hidden="true" />
      <span>
        <strong>{t(HEAD[reason as keyof typeof HEAD] ?? m.noSource)}</strong>
        {' — '}
        {noteOf(route, t)}.
      </span>
    </p>
  );
}
